import React, { useState, useEffect } from 'react'
import { apiService } from '../services/apiService'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Area, AreaChart, RadialBarChart, RadialBar
} from 'recharts'

const PredictionsPage = () => {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedModel, setSelectedModel] = useState('all')

  useEffect(() => {
    loadPredictionsData()
  }, [])

  const loadPredictionsData = async () => {
    try {
      setLoading(true)
      const response = await apiService.getDashboardData()
      setDashboardData(response)
    } catch (error) {
      console.error('Erro ao carregar predições:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando predições IA...</p>
        </div>
      </div>
    )
  }

  const predictions = dashboardData?.predictions || {}

  // Dados simulados para gráficos avançados
  const demandForecast = [
    { month: 'Jan', atual: 85, previsto: 92, confianca: 87 },
    { month: 'Fev', atual: 78, previsto: 89, confianca: 91 },
    { month: 'Mar', atual: 95, previsto: 98, confianca: 85 },
    { month: 'Abr', atual: 88, previsto: 94, confianca: 88 },
    { month: 'Mai', atual: 92, previsto: 97, confianca: 90 },
    { month: 'Jun', atual: null, previsto: 102, confianca: 85 }
  ]

  const churnRiskData = [
    { risk: 'Baixo', count: 45, color: '#22c55e' },
    { risk: 'Médio', count: 12, color: '#f59e0b' },
    { risk: 'Alto', count: predictions.churn_risk || 5, color: '#ef4444' }
  ]

  const maintenanceAlerts = [
    { vehicle: 'Caminhão MBX-1234', risk: 95, days: 3, type: 'Crítico' },
    { vehicle: 'Escavadeira CAT-5678', risk: 78, days: 7, type: 'Alto' },
    { vehicle: 'Retroescavadeira JCB-9012', risk: 65, days: 12, type: 'Médio' },
    { vehicle: 'Trator Valtra-3456', risk: 45, days: 18, type: 'Baixo' }
  ]

  const revenueProjection = [
    { period: 'Atual', value: 129800 },
    { period: '30 dias', value: predictions.revenue_projection || 149270 },
    { period: '60 dias', value: (predictions.revenue_projection || 149270) * 1.08 },
    { period: '90 dias', value: (predictions.revenue_projection || 149270) * 1.15 }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl border border-purple-200 dark:border-purple-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              🤖 Predições IA & Analytics Avançados
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Sistema de inteligência artificial para previsões e otimizações operacionais
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 px-3 py-1 rounded-full text-sm font-medium">
              🧠 IA Ativa
            </div>
            <div className="bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 px-3 py-1 rounded-full text-sm font-medium">
              📊 Modelos Treinados
            </div>
            <button
              onClick={loadPredictionsData}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              🔄 Atualizar IA
            </button>
          </div>
        </div>
      </div>

      {/* Filtros de Modelo */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">🎯 Modelos de Predição</h2>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">Todos os Modelos</option>
            <option value="demand">Previsão de Demanda</option>
            <option value="maintenance">Manutenção Preditiva</option>
            <option value="churn">Análise de Churn</option>
            <option value="revenue">Projeção de Receita</option>
          </select>
        </div>
      </div>

      {/* KPIs de IA */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <AIKPICard
          title="Precisão do Modelo"
          value="94.2%"
          change="+2.1% vs último mês"
          changeType="positive"
          icon="🎯"
          color="purple"
        />
        <AIKPICard
          title="Predições Ativas"
          value="247"
          change="12 novos insights"
          changeType="positive"
          icon="🔮"
          color="blue"
        />
        <AIKPICard
          title="Economia Prevista"
          value="R$ 45.2k"
          change="Próximos 30 dias"
          changeType="positive"
          icon="💰"
          color="green"
        />
        <AIKPICard
          title="Alertas Críticos"
          value="3"
          change="Ação necessária"
          changeType="negative"
          icon="⚠️"
          color="red"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Previsão de Demanda */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">📈 Previsão de Demanda (6 meses)</h3>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
              <span className="text-sm text-gray-600 dark:text-gray-400">Atual</span>
              <span className="w-3 h-3 bg-purple-500 rounded-full ml-4"></span>
              <span className="text-sm text-gray-600 dark:text-gray-400">Previsto</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={demandForecast}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value, name) => [
                `${value}%`,
                name === 'atual' ? 'Demanda Atual' : name === 'previsto' ? 'Demanda Prevista' : 'Confiança'
              ]} />
              <Line type="monotone" dataKey="atual" stroke="#3b82f6" strokeWidth={3} dot={{ r: 6 }} />
              <Line type="monotone" dataKey="previsto" stroke="#8b5cf6" strokeWidth={3} strokeDasharray="5 5" dot={{ r: 6 }} />
              <Area type="monotone" dataKey="confianca" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Análise de Churn */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">⚠️ Análise de Risco de Churn</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={churnRiskData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="count"
              >
                {churnRiskData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {churnRiskData.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Risco {item.risk}</span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{item.count} clientes</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Projeção de Receita */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">💰 Projeção de Receita IA</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueProjection}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip formatter={(value) => [`R$ ${value.toLocaleString('pt-BR')}`, 'Receita']} />
              <Bar dataKey="value" fill="#22c55e" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-sm text-green-800 dark:text-green-300">
              <strong>Insight IA:</strong> Crescimento projetado de 15% nos próximos 90 dias baseado em padrões históricos e tendências de mercado.
            </p>
          </div>
        </div>

        {/* Manutenção Preditiva */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">🔧 Manutenção Preditiva</h3>
          <div className="space-y-3">
            {maintenanceAlerts.map((alert, index) => (
              <div key={index} className={`p-4 rounded-lg border-l-4 ${
                alert.type === 'Crítico' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' :
                alert.type === 'Alto' ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' :
                alert.type === 'Médio' ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' :
                'border-green-500 bg-green-50 dark:bg-green-900/20'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{alert.vehicle}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Falha prevista em {alert.days} dias
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${
                      alert.type === 'Crítico' ? 'text-red-600 dark:text-red-400' :
                      alert.type === 'Alto' ? 'text-orange-600 dark:text-orange-400' :
                      alert.type === 'Médio' ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-green-600 dark:text-green-400'
                    }`}>
                      {alert.risk}%
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Risco</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insights e Recomendações */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Insights Automáticos */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">💡 Insights Automáticos</h3>
          <div className="space-y-4">
            <InsightCard
              icon="📈"
              title="Pico de Demanda"
              description="Aumento de 23% na demanda por escavadeiras previsto para dezembro"
              confidence="Alta confiança (91%)"
              type="opportunity"
            />
            <InsightCard
              icon="⚠️"
              title="Risco Operacional"
              description="3 veículos com alto risco de falha nos próximos 7 dias"
              confidence="Muito alta (96%)"
              type="warning"
            />
            <InsightCard
              icon="💰"
              title="Oportunidade"
              description="Potencial de R$ 85k em upsell baseado no comportamento dos clientes"
              confidence="Média (78%)"
              type="success"
            />
          </div>
        </div>

        {/* Recomendações */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">🎯 Recomendações IA</h3>
          <div className="space-y-4">
            <RecommendationCard
              priority="Alto"
              action="Agendar manutenção preventiva"
              target="Caminhão MBX-1234"
              impact="Evitar R$ 15k em custos de reparo"
            />
            <RecommendationCard
              priority="Médio"
              action="Contatar clientes em risco"
              target="8 clientes identificados"
              impact="Reduzir churn em 12%"
            />
            <RecommendationCard
              priority="Baixo"
              action="Expandir frota de escavadeiras"
              target="2 unidades adicionais"
              impact="Atender demanda extra de 23%"
            />
          </div>
        </div>

        {/* Modelos IA */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">🧠 Status dos Modelos</h3>
          <div className="space-y-4">
            <ModelStatus
              name="Previsão de Demanda"
              accuracy="94.2%"
              status="Ativo"
              lastTrained="2 dias atrás"
            />
            <ModelStatus
              name="Manutenção Preditiva"
              accuracy="91.7%"
              status="Ativo"
              lastTrained="5 dias atrás"
            />
            <ModelStatus
              name="Análise de Churn"
              accuracy="87.3%"
              status="Treinando"
              lastTrained="1 semana atrás"
            />
            <ModelStatus
              name="Otimização de Preços"
              accuracy="89.1%"
              status="Ativo"
              lastTrained="3 dias atrás"
            />
          </div>
        </div>
      </div>

      {/* Configurações IA */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">⚙️ Configurações de IA</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-500 dark:hover:border-purple-400 transition-colors group">
            <div className="text-center">
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">🎛️</div>
              <h4 className="font-semibold text-gray-900 dark:text-white">Ajustar Modelos</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Parâmetros e limiares</p>
            </div>
          </button>
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors group">
            <div className="text-center">
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">📊</div>
              <h4 className="font-semibold text-gray-900 dark:text-white">Treinar Modelos</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Atualizar com novos dados</p>
            </div>
          </button>
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-green-500 dark:hover:border-green-400 transition-colors group">
            <div className="text-center">
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">📈</div>
              <h4 className="font-semibold text-gray-900 dark:text-white">Relatórios IA</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Performance dos modelos</p>
            </div>
          </button>
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-orange-500 dark:hover:border-orange-400 transition-colors group">
            <div className="text-center">
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">🔔</div>
              <h4 className="font-semibold text-gray-900 dark:text-white">Alertas IA</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Configurar notificações</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}

// Componentes auxiliares
const AIKPICard = ({ title, value, change, changeType, icon, color }) => {
  const colorClasses = {
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
  }

  return (
    <div className="card">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${colorClasses[color]} text-2xl`}>{icon}</div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
      <div className="mt-4">
        <span className={`text-sm ${changeType === 'positive' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {change}
        </span>
      </div>
    </div>
  )
}

const InsightCard = ({ icon, title, description, confidence, type }) => {
  const typeClasses = {
    opportunity: 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20',
    warning: 'border-l-red-500 bg-red-50 dark:bg-red-900/20',
    success: 'border-l-green-500 bg-green-50 dark:bg-green-900/20'
  }

  return (
    <div className={`border-l-4 p-3 rounded-lg ${typeClasses[type]}`}>
      <div className="flex items-start space-x-3">
        <span className="text-xl">{icon}</span>
        <div className="flex-1">
          <p className="font-medium text-gray-900 dark:text-white">{title}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{description}</p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{confidence}</p>
        </div>
      </div>
    </div>
  )
}

const RecommendationCard = ({ priority, action, target, impact }) => {
  const priorityColors = {
    Alto: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
    Médio: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
    Baixo: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[priority]}`}>
          {priority}
        </span>
      </div>
      <h4 className="font-medium text-gray-900 dark:text-white mb-1">{action}</h4>
      <p className="text-sm text-gray-600 dark:text-gray-400">{target}</p>
      <p className="text-xs text-green-600 dark:text-green-400 mt-2">{impact}</p>
    </div>
  )
}

const ModelStatus = ({ name, accuracy, status, lastTrained }) => {
  const statusColors = {
    Ativo: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
    Treinando: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
    Inativo: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300'
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-gray-900 dark:text-white">{name}</h4>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
          {status}
        </span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-400">Precisão: {accuracy}</span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Atualizado {lastTrained}</p>
    </div>
  )
}

export default PredictionsPage